import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FinishedOutOrder } from 'src/models/OutOrder';
import { OutOrderDataSourceV3Service } from '../data-sources/v3/out-orders/out-order-data-source-v3.service';

const PAGE_SIZE_OPTIONS = [30, 60, 90];

export interface SellOrdersSortParams {
  orderBy?: "no" | "date" | "outOrderEventHistoryDispatchedAt"| "productName" | "totalQuantity";
  sortBy?: 'ASC' | 'DESC';
}

export interface SellOrdersFilterParams {
  no?: string;
  dateStartDate?: number;
  dateEndDate?: number;
  outOrderEventHistoryDispatchedAtStartDate?: number;
  outOrderEventHistoryDispatchedAtEndDate?: number;
  totalQuantity?: number;
  productName?: string;
}

export interface FinishedOutOrdersComment {
  outOrderId: string,
  commentsQty: number;
}

@Injectable({
  providedIn: 'root'
})
export class SellOrdersService {
  ordersCommentsQty$: BehaviorSubject<FinishedOutOrdersComment[]> = new BehaviorSubject([]);
  private $pageSize: BehaviorSubject<number> = new BehaviorSubject<number>(PAGE_SIZE_OPTIONS[0]);
  private $totalItems: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private $pageCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private $atLastPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private $atFirstPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private $items: BehaviorSubject<FinishedOutOrder[]> = new BehaviorSubject<FinishedOutOrder[]>([]);
  private $pageSizeOptions: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private $currentPage: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  private pageSize = PAGE_SIZE_OPTIONS[0];
  private pageSizeOptions = PAGE_SIZE_OPTIONS;
  private pageCount = 0;
  private totalItems = 0;
  public currentPage = 1;

  constructor(
    private dataSource: OutOrderDataSourceV3Service
  ) {
    this.$pageSize.next(this.pageSize);
    this.$pageSizeOptions.next(this.pageSizeOptions);
  }

  async init() {
    return this.getPage(1);
  }

  async setPageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$pageSize.next(this.pageSize);
    await this.getPage(1);
  }

  async reload(filter: SellOrdersFilterParams = null, sortParams?: SellOrdersSortParams) {
    return this.getPage(this.currentPage, filter, sortParams);
  }

  async getNextPage(filter: SellOrdersFilterParams = null, sortParams?: SellOrdersSortParams) {
    if (this.currentPage < this.pageCount) {
      return this.getPage(this.currentPage + 1, filter, sortParams);
    }
    throw Error('???');
  }

  async getPreviousPage(filter: SellOrdersFilterParams = null, sortParams?: SellOrdersSortParams) {
    if (this.currentPage > 1) {
      return this.getPage(this.currentPage - 1, filter, sortParams);
    }
    throw Error('???');
  }

  async setPage(page: number, filter: SellOrdersFilterParams = null, sortParams?: SellOrdersSortParams) {
    this.currentPage = page;
    return this.getPage(this.currentPage, filter, sortParams);
  }

  private async getPage(page: number, filter: SellOrdersFilterParams = {}, sortParams?: SellOrdersSortParams) {
    this.currentPage = page;
    const params = {
      limit: this.pageSize,
      offset: (this.currentPage - 1) * this.pageSize
    };
    Object.assign(params, filter);
    Object.assign(params, sortParams);
    const response = await this.dataSource.getFinishedOutOrders(params);
    this.totalItems = response.count;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$items.next(response.values);
    this.$pageCount.next(this.pageCount);
    this.$totalItems.next(this.totalItems);
    this.$currentPage.next(this.currentPage);
    this.$atLastPage.next(this.pageCount === 0 || this.currentPage === this.pageCount);
    this.$atFirstPage.next(this.currentPage === 1);
    this.getOrderCommentsQty(response.values);
  }

  getOrderCommentsQty(outOrders: FinishedOutOrder[]) {
    this.ordersCommentsQty$.next([]);
    if (outOrders.length) {
      const orderIds = getOrdersId(outOrders);
      this.dataSource.getFinishedOutOrderComments(orderIds).subscribe(comments => {
        this.ordersCommentsQty$.next(comments);
      });
    }
  }

  get currentPage$(): Observable<number> {
    return this.$currentPage;
  }

  get atFirstPage$(): Observable<boolean> {
    return this.$atFirstPage;
  }

  get atLastPage$(): Observable<boolean> {
    return this.$atLastPage;
  }

  get totalItems$(): Observable<number> {
    return this.$totalItems;
  }

  get pageCount$(): Observable<number> {
    return this.$pageCount;
  }

  get items$(): Observable<FinishedOutOrder[]> {
    return this.$items;
  }

  get pageSize$(): Observable<number> {
    return this.$pageSize;
  }

  get pageSizeOptions$(): Observable<number[]> {
    return this.$pageSizeOptions;
  }
}

function getOrdersId(outOrders: FinishedOutOrder[]) {
  return outOrders.map(outOrder => outOrder.id);
}
